import { z } from 'zod';
import { configStatusSchema, refineSiemConfig, siemConfigBaseSchema, SIEM_HEC_TOKEN_MASK, siemProtocolSchema, } from '../settings';
import { severitySchema } from '../shared/severity';
/**
 * Public SIEM-forwarding settings for `/api/v1` (KIR-4083).
 *
 * WWT provisions one Kirin organization per demo tenant and needs to point each
 * one at that tenant's collector from a script, so the config this endpoint
 * writes is the same row the dashboard's Connect SIEM modal writes — the same
 * `settings` row, the same validation, the same encryption. Only the shape at
 * the boundary is this file's concern.
 *
 * **The HEC token never crosses this boundary outbound.** The read schema has no
 * `hecToken` field at all; it reports `hecTokenSet` instead. That is not
 * tidiness — the token is a bearer secret, and a GET is the one call a customer
 * is most likely to log, paste into a ticket, or hand to a connector's debug
 * screen.
 */
/** Read shape. Deliberately has no `hecToken` — see the module doc. */
export const v1SiemSettingSchema = z
    .object({
    enabled: z
        .boolean()
        .describe('Whether forwarding is active. `false` or a 404 both mean nothing is being sent.'),
    protocol: siemProtocolSchema.describe('Transport. `tcp`/`udp` are RFC 5424 syslog to `host`:`port`; `hec` is a Splunk HTTP Event Collector POST to `endpointUrl`.'),
    host: z
        .string()
        .nullable()
        .describe('Syslog destination host. `null` for a HEC config.'),
    port: z
        .number()
        .int()
        .nullable()
        .describe('Syslog destination port. `null` for a HEC config.'),
    endpointUrl: z
        .string()
        .nullable()
        .describe('Splunk HEC endpoint URL. `null` for a syslog config.'),
    hecTokenSet: z
        .boolean()
        .describe('Whether a HEC token is stored. The token itself is never returned. To keep the stored token when updating, send the literal `' +
        SIEM_HEC_TOKEN_MASK +
        '`.'),
    insecureSkipVerify: z
        .boolean()
        .describe('HEC only. When `true`, TLS certificate verification is skipped for this endpoint. Insecure — the bearer token rides that connection.'),
    minSeverity: severitySchema.describe('Alerts below this severity are not forwarded. Does not apply to audit events, which have no severity.'),
    forwardAlerts: z
        .boolean()
        .describe('Whether scan alerts are forwarded to this destination.'),
    forwardAuditLogs: z
        .boolean()
        .describe('Whether audit-log events are forwarded. Events are batched and sent about once a minute, so allow up to ~60s before an audited change appears in your collector.'),
    configStatus: configStatusSchema.describe('`ok` once a send has succeeded, `error` after a permanent failure (bad host, refused connection, rejected token), `untested` before either.'),
})
    .meta({
    id: 'V1SiemSetting',
    title: 'SIEM setting',
    description: "The organization's SIEM forwarding configuration. The HEC token is never returned.",
    examples: [
        {
            enabled: true,
            protocol: 'hec',
            host: null,
            port: null,
            endpointUrl: 'https://splunk.example.com:8088/services/collector/event',
            hecTokenSet: true,
            insecureSkipVerify: false,
            minSeverity: 'high',
            forwardAlerts: true,
            forwardAuditLogs: true,
            configStatus: 'ok',
        },
    ],
});
/**
 * Write shape.
 *
 * Reuses {@link refineSiemConfig} rather than restating the transport rules, so
 * this surface and the dashboard's cannot disagree about what a valid config is.
 * `configStatus` and `hecTokenSet` are omitted because both are derived: status
 * is set by delivery outcomes, and whether a token is stored is a fact about
 * storage, not an input.
 */
export const v1SiemSettingUpsertSchema = siemConfigBaseSchema
    .omit({ configStatus: true, hecTokenSet: true })
    .superRefine(refineSiemConfig)
    .meta({
    id: 'V1SiemSettingUpsert',
    title: 'SIEM setting update',
    description: 'A complete SIEM configuration. Send `host` + `port` for tcp/udp, or `endpointUrl` + `hecToken` for hec — mixing the two is rejected.',
    examples: [
        {
            protocol: 'hec',
            endpointUrl: 'https://splunk.example.com:8088/services/collector/event',
            hecToken: '<your-hec-token>',
            insecureSkipVerify: false,
            minSeverity: 'high',
            forwardAlerts: true,
            forwardAuditLogs: true,
        },
        {
            protocol: 'tcp',
            host: 'siem.example.com',
            port: 6514,
            minSeverity: 'medium',
            forwardAlerts: true,
            forwardAuditLogs: false,
        },
    ],
});
/**
 * Result of `?test=only` — a connectivity probe that persists nothing.
 *
 * A failed probe is a 200 with `success: false`, not a 4xx: the request was
 * well-formed and Kirin did exactly what was asked. The failure is the
 * destination's, and `error` carries the reason to show the operator.
 */
export const v1SiemTestResultSchema = z
    .object({
    success: z
        .boolean()
        .describe('Whether the synthetic test event reached the destination.'),
    error: z
        .string()
        .nullable()
        .describe('Why the probe failed. `null` when it succeeded.'),
})
    .meta({
    id: 'V1SiemTestResult',
    title: 'SIEM connection test result',
    description: 'The outcome of a connectivity probe. Persists nothing.',
    examples: [
        { success: true, error: null },
        {
            success: false,
            error: 'dial tcp 203.0.113.10:6514: connection refused',
        },
    ],
});
//# sourceMappingURL=settings.js.map